import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Layout from "../../components/layout/Layout";
import kitchensApi from "../../api/kitchens";
import useAuth from "../../hooks/useAuth";
import { Kitchen } from "../../types/auth";

const DEFAULT_SCHEDULE = [
  { dayOfWeek: "monday", opensAt: "07:00", closesAt: "22:00", isClosed: false },
  { dayOfWeek: "tuesday", opensAt: "07:00", closesAt: "22:00", isClosed: false },
  { dayOfWeek: "wednesday", opensAt: "07:00", closesAt: "22:00", isClosed: false },
  { dayOfWeek: "thursday", opensAt: "07:00", closesAt: "22:00", isClosed: false },
  { dayOfWeek: "friday", opensAt: "07:00", closesAt: "22:00", isClosed: false },
  { dayOfWeek: "saturday", opensAt: "07:00", closesAt: "22:00", isClosed: false },
  { dayOfWeek: "sunday", opensAt: "07:00", closesAt: "22:00", isClosed: false },
];

const emptyForm = {
  name: "",
  address: "",
  pin: "",
  capacity: 0,
  kitchenLevel: 1,
  isActive: true,
  isOpen: true,
  latitude: "",
  longitude: "",
  operatingHours: DEFAULT_SCHEDULE,
  logisticsConfig: {
    basePrepTime: 10,
    busyThreshold: 5,
    busyPenalty: 5,
    largeCartThreshold: 2,
    largeCartPenalty: 3,
    isRainMode: false,
    rainPenalty: 10,
    baseDeliveryTimeNear: 15,
    baseDeliveryTimeFar: 18,
  },
};

const Kitchens = () => {
  const { refreshKitchens } = useAuth();
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingKitchen, setEditingKitchen] = useState<Kitchen | null>(null);
  const [formData, setFormData] = useState<any>(emptyForm);

  const fetchKitchens = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await kitchensApi.getAdminKitchens();
      setKitchens(response.kitchens || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to load kitchens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchKitchens();
  }, []);

  const openCreateDialog = () => {
    setEditingKitchen(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (kitchen: Kitchen) => {
    setEditingKitchen(kitchen);
    const existingHours = (kitchen.operatingHours && kitchen.operatingHours.length > 0)
      ? kitchen.operatingHours
      : DEFAULT_SCHEDULE;

    setFormData({
      name: kitchen.name || "",
      address: kitchen.address || "",
      pin: kitchen.pin || "",
      capacity: kitchen.capacity || 0,
      kitchenLevel: kitchen.kitchenLevel ?? 1,
      isActive: kitchen.isActive,
      isOpen: kitchen.rawIsOpen !== undefined ? kitchen.rawIsOpen : kitchen.isOpen,
      latitude: kitchen.coordinates?.latitude ?? "",
      longitude: kitchen.coordinates?.longitude ?? "",
      operatingHours: JSON.parse(JSON.stringify(existingHours)),
      logisticsConfig: kitchen.logisticsConfig ? { ...kitchen.logisticsConfig } : { ...emptyForm.logisticsConfig },
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingKitchen(null);
  };

  const handleHourChange = (index: number, field: string, value: any) => {
    const updated = [...formData.operatingHours];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, operatingHours: updated });
  };

  const handleResetToDefaultHours = () => {
    setFormData({ ...formData, operatingHours: JSON.parse(JSON.stringify(DEFAULT_SCHEDULE)) });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        pin: formData.pin,
        capacity: Number(formData.capacity) || 0,
        kitchenLevel: Number(formData.kitchenLevel) || 1,
        isActive: Boolean(formData.isActive),
        isOpen: Boolean(formData.isOpen),
        operatingHours: formData.operatingHours,
        coordinates: {
          latitude: formData.latitude === "" ? undefined : Number(formData.latitude),
          longitude: formData.longitude === "" ? undefined : Number(formData.longitude),
        },
        logisticsConfig: formData.logisticsConfig,
      };

      if (editingKitchen?._id) {
        await kitchensApi.updateKitchen(editingKitchen._id, payload);
      } else {
        await kitchensApi.createKitchen(payload);
      }

      closeDialog();
      await fetchKitchens();
      await refreshKitchens();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save kitchen");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (kitchenId: string) => {
    try {
      await kitchensApi.deleteKitchen(kitchenId);
      await fetchKitchens();
      await refreshKitchens();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to deactivate kitchen");
    }
  };

  const handleToggleOpen = async (kitchen: Kitchen) => {
    try {
      const currentRaw = kitchen.rawIsOpen !== undefined ? kitchen.rawIsOpen : kitchen.isOpen;
      await kitchensApi.updateKitchen(kitchen._id, { isOpen: !currentRaw });
      await fetchKitchens();
      await refreshKitchens();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to update kitchen");
    }
  };

  return (
    <Layout>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Kitchens
          </Typography>
          <Typography color="text.secondary">
            Manage active kitchens, daily operational schedules (7 AM – 10 PM), and real-time status.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreateDialog}>
          Add Kitchen
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>PIN</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Operational State</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Manual Open</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {kitchens.map((kitchen) => (
              <TableRow key={kitchen._id}>
                <TableCell>
                  <Typography fontWeight={600}>{kitchen.name}</Typography>
                </TableCell>
                <TableCell>{kitchen.address}</TableCell>
                <TableCell>{kitchen.pin}</TableCell>
                <TableCell>{kitchen.kitchenLevel ?? "-"}</TableCell>
                <TableCell>
                  {kitchen.operationalStatus === "OPEN" ? (
                    <Chip size="small" color="success" label="🟢 Open" />
                  ) : kitchen.operationalStatus === "TEMPORARILY_CLOSED" ? (
                    <Chip size="small" color="warning" label="🟡 Paused (Working Hours)" />
                  ) : (
                    <Chip
                      size="small"
                      color="default"
                      label={`🌙 Off-Hours (${kitchen.nextOpensAtFormatted || "Closed"})`}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={kitchen.isActive ? "success" : "default"}
                    label={kitchen.isActive ? "Active" : "Inactive"}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={Boolean(kitchen.rawIsOpen !== undefined ? kitchen.rawIsOpen : kitchen.isOpen)}
                    disabled={!kitchen.isActive}
                    onChange={() => void handleToggleOpen(kitchen)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => openEditDialog(kitchen)}>
                      Edit
                    </Button>
                    {kitchen.isActive && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => void handleDeactivate(kitchen._id)}
                      >
                        Deactivate
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && kitchens.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No kitchens found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingKitchen ? "Edit Kitchen & Operating Hours" : "Create Kitchen"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              General Information
            </Typography>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="PIN"
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                fullWidth
              />
              <TextField
                label="Kitchen Level"
                type="number"
                value={formData.kitchenLevel}
                onChange={(e) => setFormData({ ...formData, kitchenLevel: e.target.value })}
                fullWidth
              />
              <TextField
                label="Capacity"
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Latitude"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                fullWidth
              />
              <TextField
                label="Longitude"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isOpen}
                    onChange={(e) => setFormData({ ...formData, isOpen: e.target.checked })}
                  />
                }
                label="Manual Open Switch (Turn off to pause during working hours)"
              />
            </Stack>

            {/* Operating Hours Scheduler */}
            <Box sx={{ pt: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Operating Hours Schedule (IST)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Orders will automatically open and close according to this daily schedule.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={handleResetToDefaultHours}>
                  Set 7 AM – 10 PM Everyday
                </Button>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1.5}>
                  {formData.operatingHours?.map((oh: any, index: number) => (
                    <Stack
                      key={oh.dayOfWeek}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Typography sx={{ width: 110, fontWeight: 600, textTransform: "capitalize" }}>
                        {oh.dayOfWeek}
                      </Typography>
                      <TextField
                        label="Opens At (HH:mm)"
                        size="small"
                        value={oh.opensAt}
                        disabled={oh.isClosed}
                        placeholder="07:00"
                        onChange={(e) => handleHourChange(index, "opensAt", e.target.value)}
                        sx={{ width: 140 }}
                      />
                      <TextField
                        label="Closes At (HH:mm)"
                        size="small"
                        value={oh.closesAt}
                        disabled={oh.isClosed}
                        placeholder="22:00"
                        onChange={(e) => handleHourChange(index, "closesAt", e.target.value)}
                        sx={{ width: 140 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!oh.isClosed}
                            onChange={(e) => handleHourChange(index, "isClosed", !e.target.checked)}
                          />
                        }
                        label={oh.isClosed ? "Closed" : "Open"}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            {/* Logistics Config */}
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                Logistics & ETA Configuration
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Base Prep Time (Mins)"
                      type="number"
                      size="small"
                      value={formData.logisticsConfig?.basePrepTime ?? 10}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, basePrepTime: Number(e.target.value) }
                      })}
                      fullWidth
                    />
                    <TextField
                      label="Delivery Time Near (<= 2km)"
                      type="number"
                      size="small"
                      value={formData.logisticsConfig?.baseDeliveryTimeNear ?? 15}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, baseDeliveryTimeNear: Number(e.target.value) }
                      })}
                      fullWidth
                    />
                    <TextField
                      label="Delivery Time Far (> 2km)"
                      type="number"
                      size="small"
                      value={formData.logisticsConfig?.baseDeliveryTimeFar ?? 18}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, baseDeliveryTimeFar: Number(e.target.value) }
                      })}
                      fullWidth
                    />
                  </Stack>
                  
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Busy Threshold (Active Orders)"
                      type="number"
                      size="small"
                      value={formData.logisticsConfig?.busyThreshold ?? 5}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, busyThreshold: Number(e.target.value) }
                      })}
                      fullWidth
                    />
                    <TextField
                      label="Busy Penalty (Mins)"
                      type="number"
                      size="small"
                      value={formData.logisticsConfig?.busyPenalty ?? 5}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, busyPenalty: Number(e.target.value) }
                      })}
                      fullWidth
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Large Cart Threshold (Items)"
                      type="number"
                      size="small"
                      value={formData.logisticsConfig?.largeCartThreshold ?? 2}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, largeCartThreshold: Number(e.target.value) }
                      })}
                      fullWidth
                    />
                    <TextField
                      label="Large Cart Penalty (Mins)"
                      type="number"
                      size="small"
                      value={formData.logisticsConfig?.largeCartPenalty ?? 3}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, largeCartPenalty: Number(e.target.value) }
                      })}
                      fullWidth
                    />
                  </Stack>
                  
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(formData.logisticsConfig?.isRainMode)}
                          onChange={(e) => setFormData({
                            ...formData,
                            logisticsConfig: { ...formData.logisticsConfig, isRainMode: e.target.checked }
                          })}
                        />
                      }
                      label="Enable Rain Mode Delay"
                    />
                    <TextField
                      label="Rain Penalty (Mins)"
                      type="number"
                      size="small"
                      disabled={!formData.logisticsConfig?.isRainMode}
                      value={formData.logisticsConfig?.rainPenalty ?? 10}
                      onChange={(e) => setFormData({
                        ...formData,
                        logisticsConfig: { ...formData.logisticsConfig, rainPenalty: Number(e.target.value) }
                      })}
                      sx={{ width: 200 }}
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={() => void handleSave()} disabled={saving} variant="contained">
            {editingKitchen ? "Save Changes" : "Create Kitchen"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Kitchens;
